import { BlogPost } from '../types/blog';

// Import all blog posts - Vite will handle this at build time
const blogPostsModules = import.meta.glob('../content/blog/*.md', {
    eager: true,
    query: '?raw',
    import: 'default'
});

/**
 * Parse frontmatter from markdown content (browser-safe version without Buffer)
 * Supports YAML block sequences (- item), nested objects, and inline arrays.
 */
function parseFrontmatter(content: string): { data: Record<string, unknown>; content: string } {
    const frontmatterRegex = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/;
    const match = content.match(frontmatterRegex);

    if (!match) {
        return { data: {}, content };
    }

    const [, frontmatterStr, markdownContent] = match;
    const data: Record<string, unknown> = {};

    let currentTopKey: string | null = null;
    let currentObject: Record<string, unknown> | null = null;
    let currentNestedArrayKey: string | null = null;
    let currentArrayItem: Record<string, unknown> | null = null;

    const lines = frontmatterStr.split(/\r?\n/);

    function parseValue(raw: string): unknown {
        let value: unknown = raw;
        if (typeof value === 'string' && ((value.startsWith('"') && value.endsWith('"')) ||
            (value.startsWith("'") && value.endsWith("'")))) {
            value = (value as string).slice(1, -1);
        }
        if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
            value = (value as string).slice(1, -1)
                .split(',')
                .map(v => v.trim().replace(/^["']|["']$/g, ''))
                .filter(Boolean);
        }
        if (value === 'true') return true;
        if (value === 'false') return false;
        return value;
    }

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) continue;

        const indent = line.match(/^(\s*)/)?.[1].length ?? 0;
        const trimmed = line.trim();

        if (indent === 0) {
            // Top-level key
            currentNestedArrayKey = null;
            currentArrayItem = null;
            const colonIndex = trimmed.indexOf(':');
            if (colonIndex === -1) continue;

            const key = trimmed.substring(0, colonIndex).trim();
            const rawValue = trimmed.substring(colonIndex + 1).trim();

            if (!rawValue) {
                // No value: could be an object or array — determined by next lines
                currentTopKey = key;
                currentObject = null;
                data[key] = null;
            } else {
                currentTopKey = null;
                currentObject = null;
                data[key] = parseValue(rawValue);
            }
        } else if (indent === 2) {
            if (trimmed.startsWith('- ')) {
                // Block sequence item under a top-level key
                if (currentTopKey) {
                    if (!Array.isArray(data[currentTopKey])) {
                        data[currentTopKey] = [];
                        currentObject = null;
                    }
                    const itemContent = trimmed.slice(2).trim();
                    const colonIndex = itemContent.indexOf(':');

                    if (colonIndex !== -1) {
                        // Array item is an object (e.g., "- question: ...")
                        currentArrayItem = {};
                        const key = itemContent.substring(0, colonIndex).trim();
                        const value = itemContent.substring(colonIndex + 1).trim();
                        currentArrayItem[key] = parseValue(value);
                        (data[currentTopKey] as unknown[]).push(currentArrayItem);
                    } else {
                        // Array item is a simple string
                        (data[currentTopKey] as unknown[]).push(itemContent);
                        currentArrayItem = null;
                    }
                }
            } else {
                // Nested key inside a top-level object
                currentNestedArrayKey = null;
                currentArrayItem = null;
                if (currentTopKey) {
                    if (!currentObject) {
                        currentObject = {};
                        data[currentTopKey] = currentObject;
                    }
                    const colonIndex = trimmed.indexOf(':');
                    if (colonIndex === -1) continue;

                    const key = trimmed.substring(0, colonIndex).trim();
                    const rawValue = trimmed.substring(colonIndex + 1).trim();

                    if (!rawValue) {
                        // Nested key with no value: likely a nested array
                        currentNestedArrayKey = key;
                        currentObject[key] = null;
                    } else {
                        currentObject[key] = parseValue(rawValue);
                    }
                }
            }
        } else if (indent >= 4) {
            // Could be a property of an array item object or nested array items
            if (currentArrayItem && trimmed.indexOf(':') !== -1 && indent === 4) {
                // Property of current array item object
                const colonIndex = trimmed.indexOf(':');
                const key = trimmed.substring(0, colonIndex).trim();
                const value = trimmed.substring(colonIndex + 1).trim();
                currentArrayItem[key] = parseValue(value);
            } else if (currentObject && currentNestedArrayKey && trimmed.startsWith('- ') && indent === 4) {
                // Block sequence item under a nested key (e.g. seo.keywords)
                if (!Array.isArray(currentObject[currentNestedArrayKey])) {
                    currentObject[currentNestedArrayKey] = [];
                }
                (currentObject[currentNestedArrayKey] as unknown[]).push(trimmed.slice(2).trim());
            }
        }
    }

    return { data, content: markdownContent.trim() };
}

/**
 * Get all blog posts from the content directory
 */
export function getAllBlogPosts(): BlogPost[] {
    const posts: BlogPost[] = [];

    for (const path in blogPostsModules) {
        const fileContent = blogPostsModules[path] as string;
        const { data, content } = parseFrontmatter(fileContent);

        posts.push({
            ...data,
            content,
        } as BlogPost);
    }

    // Sort by date (newest first)
    return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * Get a single blog post by slug
 */
export function getBlogPostBySlug(slug: string): BlogPost | undefined {
    const posts = getAllBlogPosts();
    return posts.find(post => post.slug === slug);
}

/**
 * Get related posts based on category and tags
 */
export function getRelatedPosts(currentPost: BlogPost, limit: number = 3): BlogPost[] {
    const allPosts = getAllBlogPosts();
    const result: BlogPost[] = [];

    // First, check if there are manually specified related articles
    if (currentPost.relatedArticles && currentPost.relatedArticles.length > 0) {
        const manuallyRelated = allPosts.filter(post =>
            currentPost.relatedArticles?.includes(post.slug) && post.slug !== currentPost.slug
        );
        result.push(...manuallyRelated.slice(0, limit));
    }

    // If we need more articles, fill with scoring-based recommendations
    if (result.length < limit) {
        const scoredPosts = allPosts
            .filter(post => post.slug !== currentPost.slug && !result.some(r => r.slug === post.slug) && post.tags && currentPost.tags)
            .map(post => {
                let score = 0;

                // Same category gets high score
                const currentCategories = Array.isArray(currentPost.category) ? currentPost.category : [currentPost.category];
                const postCategories = Array.isArray(post.category) ? post.category : [post.category];
                const sharedCategories = postCategories.filter(cat => currentCategories.includes(cat));
                if (sharedCategories.length > 0) {
                    score += sharedCategories.length * 10;
                }

                // Shared tags
                if (post.tags && currentPost.tags) {
                    const sharedTags = post.tags.filter(tag => currentPost.tags.includes(tag));
                    score += sharedTags.length * 5;
                }

                return { post, score };
            })
            .filter(({ score }) => score > 0)
            .sort((a, b) => b.score - a.score);

        result.push(...scoredPosts.slice(0, limit - result.length).map(({ post }) => post));
    }

    return result.slice(0, limit);
}

/**
 * Get all unique categories with post counts
 * Supports both single category (string) and multiple categories (string[])
 */
export function getAllCategories() {
    const posts = getAllBlogPosts();
    const categoryMap = new Map<string, number>();

    posts.forEach(post => {
        if (post.category) {
            const categories = Array.isArray(post.category) ? post.category : [post.category];
            categories.forEach(cat => {
                if (cat) {
                    const count = categoryMap.get(cat) || 0;
                    categoryMap.set(cat, count + 1);
                }
            });
        }
    });

    return Array.from(categoryMap.entries())
        .filter(([name]) => name !== undefined && name !== null)
        .map(([name, count]) => ({
            name,
            slug: name.toLowerCase().replace(/\s+/g, '-'),
            count,
        }));
}

/**
 * Get all unique tags
 */
export function getAllTags(): string[] {
    const posts = getAllBlogPosts();
    const tagsSet = new Set<string>();

    posts.forEach(post => {
        post.tags.forEach(tag => tagsSet.add(tag));
    });

    return Array.from(tagsSet).sort();
}

/**
 * Calculate estimated reading time from content
 */
export function calculateReadingTime(content: string): string {
    const wordsPerMinute = 200;
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min`;
}
